import { BlockQuoteContainer } from "shared/components/BlockQuote";
import { ButtonGroupContainer } from "shared/components/ButtonGroup";
import { createButtonProps } from "shared/components/Button";
import { SiteLink } from "shared/components/SiteLink";
import { ContactCardContainer } from "shared/components/ContactCard";
import { createElement, useEffect, useRef } from "react";
import { ExpandablesContainer } from "shared/components/Expandables";
import { ImageContainer } from "shared/components/Image";
import { MaxWidth } from "shared/components/MaxWidth";
import { MessageContainer } from "shared/components/Message";
import { NewsletterSectionContainer } from "shared/components/NewsletterSection";
import {
  PERSON_TYPE,
  EMAIL_TYPE,
  PHONE_TYPE,
  LOCATION_TYPE,
  CLARIAH_LAPOSTA_NEWSLETTER_FORM_URL,
} from "shared/utils/constants";
import { RichText } from "shared/components/RichText";
import { SMALL, MUX } from "shared/utils/constants";
import { SurveyMonkeyEmbedContainer } from "shared/components/SurveyMonkeyEmbed";
import { TableContainer } from "shared/components/Table";
import { TYPE_LINK, TYPE_BUTTON } from "shared/utils/constants";
import { VideoContainer } from "shared/components/Video";
import BaseBlockContent from "@sanity/block-content-to-react";
import BlockContent from "@sanity/block-content-to-react";
import styles from "./Content.module.css";
import { useAppContext } from "../utils/useAppContext";
import {
  NO_CONTENT_FALLBACK_BODY,
  NO_CONTENT_FALLBACK_TITLE,
  translate,
} from "shared/utils/translate";
import { useRouter } from "next/router";

export const Content = ({ data, dropCapDisabled }) => {
  const containerRef = useRef();
  const { locale } = useRouter();
  const { scope } = useAppContext();

  useEffect(() => {
    if (!dropCapDisabled && containerRef.current) {
      const firstParagraph = containerRef.current.getElementsByTagName("p")[0];
      if (
        firstParagraph &&
        !firstParagraph.classList.contains("dropcap") &&
        firstParagraph.textContent.length > 200
      ) {
        firstParagraph.classList.add("dropcap");
      }
    }
  });

  return (
    <div className={styles.container} ref={containerRef}>
      {data ? (
        <BaseBlockContent
          blocks={data || []}
          serializers={getSerializers(scope)}
        />
      ) : (
        <MessageContainer
          title={translate(NO_CONTENT_FALLBACK_TITLE, locale)}
          body={translate(NO_CONTENT_FALLBACK_BODY, locale)}
        />
      )}
    </div>
  );
};

export const onlyLinkSerializers = {
  marks: {
    link: ({ mark, children }) => (
      <SiteLink href={mark.href}>{children}</SiteLink>
    ),
  },
};

function getSerializers(scope) {
  return {
    types: {
      block(props) {
        if (/^h\d/.test(props.node.style)) {
          return (
            <MaxWidth size={SMALL}>
              <RichText>
                {createElement(props.node.style, {}, props.children)}
              </RichText>
            </MaxWidth>
          );
        }

        return (
          <MaxWidth size={SMALL}>
            <RichText>
              {BlockContent.defaultSerializers.types.block(props)}
            </RichText>
          </MaxWidth>
        );
      },
      customImageWithDetails({ node }) {
        return (
          <ImageContainer
            alt={node.alt}
            caption={node.caption}
            credits={node.credits}
            size={node.size}
            src={node.image?.url}
          />
        );
      },
      video({ node }) {
        const isVideoFile = node.video?.condition == "videoFile";
        return (
          <VideoContainer
            caption={node.caption}
            credits={node.credits}
            size={node.size}
            type={isVideoFile ? MUX : node.video?.videoId?.service}
            thumbTime={isVideoFile && node.video?.videoFile?.thumbTime}
            videoId={
              isVideoFile
                ? node.video?.videoFile?.playbackId
                : node.video?.videoId?.id
            }
          />
        );
      },
      quote({ node }) {
        return (
          <BlockQuoteContainer
            cite={node.cite}
            quote={node.quote}
            size={node.size}
          />
        );
      },
      expandables({ node }) {
        const expandableItems = node.expandables.map(({ text, title }) => ({
          text,
          title,
        }));

        return (
          <ExpandablesContainer title={node.title} items={expandableItems} />
        );
      },

      buttons({ node }) {
        const buttonItems = node.buttons
          .map((x) => ({ ...x, type: TYPE_BUTTON }))
          .map(createButtonProps);

        return <ButtonGroupContainer items={buttonItems} />;
      },

      strongLinks({ node }) {
        const buttonItems = node.buttons
          .map((x) => ({ ...x, type: TYPE_LINK }))
          .map(createButtonProps);

        return <ButtonGroupContainer items={buttonItems} />;
      },

      contactCard({ node }) {
        return (
          <ContactCardContainer
            description={node.description}
            image={node.image?.url}
            link={{ href: node.link }}
            meta={node.meta}
            name={node.name}
            title={node.title}
          />
        );
      },

      contactPersonCard({ node }) {
        const titlesObject =
          node?.person?.titles?.length > 0 ? node?.person?.titles[0] || {} : {};

        return (
          <ContactCardContainer
            title={node.title}
            name={node.person.name}
            link={{
              type: PERSON_TYPE,
              href: node.person?.slug?.current,
            }}
            description={
              node?.person?.titles?.length > 0
                ? [titlesObject.title, titlesObject.organization]
                    .filter(Boolean)
                    .join(", ")
                : null
            }
            meta={[EMAIL_TYPE, PHONE_TYPE, LOCATION_TYPE].map((type) => ({
              type,
              label: node.person[type],
            }))}
            image={node.person.image?.url}
          />
        );
      },

      eventTable({ node }) {
        return (
          <TableContainer
            title={node.title}
            fields={(node.fields || []).map(({ label, value }) => {
              return {
                columns: [{ value: label }, { value }],
              };
            })}
          />
        );
      },

      surveyMonkeyEmbed({ node }) {
        return <SurveyMonkeyEmbedContainer src={node.src} />;
      },

      newsletter({ node }) {
        return (
          <NewsletterSectionContainer
            url={CLARIAH_LAPOSTA_NEWSLETTER_FORM_URL}
          />
        );
      },
    },
    list: ({ type, children, level }) =>
      level === 1 ? (
        <MaxWidth size={SMALL}>
          <RichText>
            {type === "bullet" ? <ul>{children}</ul> : <ol>{children}</ol>}
          </RichText>
        </MaxWidth>
      ) : (
        <>{type === "bullet" ? <ul>{children}</ul> : <ol>{children}</ol>}</>
      ),
    listItem: (props) => <li>{props.children}</li>,
    marks: {
      link: ({ mark, children }) => (
        <SiteLink href={mark.href}>{children}</SiteLink>
      ),
    },
  };
}
